import { create } from "zustand";
import { useAuth } from "../../auth/auth-provider/auth-provider";
import { PostGoldStepPrice } from "../../api-layer/step-price-api/PostGoldStepPrice";
import { QuantityDefinitionPatch } from "../../api-layer/quantity-definition-api/QuantityDefinitionPatch";
import { PatchGoldPriceIsActive } from "../../api-layer/gold-price-is-active-api/PatchGoldPriceIsActive";
import { PostGoldPrice } from "../../api-layer/gold-price-api/PostGoldPrice";
import { GetGoldPriceType } from "../../api-layer/gold-price-type-api/GetGoldPriceType";
import GeneralGetGoldPriceIsActive from "../../api-layer/gold-price-is-active-api/GeneralGetGoldPriceIsActive";
import GeneralPatchGoldPriceIsActive from "../../api-layer/gold-price-is-active-api/GeneralPatchGoldPriceIsActive";
import { normalizeQuantity } from "./normalizers/normalizeQuantity";
import { bulkPostGoldPricesV2 } from "./actions/bulkPostGoldPricesV2";

import { findNodeId } from "./helpers/findNodeId";
import { applyPatchCore } from "./patches/applyPatch";
import { fetchSnapshotV2 } from "./actions/fetchSnapshotV2";
import { PostLastGoldPrice } from "../../api-layer/last-gold-price-api/PostLastGoldPrice";
import { PostParentGoldPrice } from "../../api-layer/parent-gold-price-api/PostParentGoldPrice";

import { mergeLastPriceUpdate } from "./lastPriceUpdate";

const clone = (obj) => structuredClone(obj);

export const usePricingStore = create((set, get) => ({
    // -------------------------
    // STATE
    // -------------------------
    snapshot: null,
    loading: false,

    quantitiesById: {},

    user: null,
    mode: "user",

    goldPriceTypes: null,
    adminGoldPriceTypes: null,
    lastGoldPriceCreationDate: null,
    lastPriceUpdate: null,
    
    buyType: null,
    sellType: null,

    realtime: {
        sequenceByStream: {}
    },

    loadingStates: {}, // Track loading per component: { 'buy-123': true/false }
    errorStates: {},   // Track errors per component: { 'buy-123': 'Error message' }
    
    getToggleKey: (q, priceType) => {
        const side = priceType === 1 ? 'buy' : 'sell';
        return `${side}-${q.id}`;
    },
    
    clearToggleError: (key) => {
        set(state => ({
            errorStates: { ...state.errorStates, [key]: null }
        }));
    },

    initPricing: async () => {
        await Promise.all([
            get().fetchSnapshotV2(),
            get().fetchGoldPriceTypes(),
            get().fetchAdminGoldPriceTypes(),
        ]);
    },

    getQuantity: (quantity) => {
        return get().quantitiesById[quantity.nodeId];
    },

    getPrice: (nodeId, side) => {
        const q = get().quantitiesById[nodeId];

        if (!q) return null;

        return q.prices?.[side].price ?? null;
    },

    isPriceActive: (nodeId, side) => {
        const q = get().quantitiesById[nodeId];

        return q.active?.[side]?.isActive ?? false;
    },

    fetchGoldPriceTypes: async () => {
        try {
            const res = await GetGoldPriceType();
            const data = res.data;
            // console.log('data of price types: ', res.data)
            let buy = null;
            let sell = null;

            for (let type of data) {
                if (type.name === "online buy") buy = type;
                if (type.name === "online sell") sell = type;
            }

            set({
                goldPriceTypes: data,
                buyType: buy,
                sellType: sell,
            });

        } catch (e) {
            // console.error(e);
        }
    },

    fetchAdminGoldPriceTypes: async () => {
        try {
            const res = await GetGoldPriceType("admin");

            set({
                adminGoldPriceTypes: res.data,
            });

        } catch (e) {
            // console.error(e);
        }
    },

    setUser: (user) => {
        set(state => ({
            user,
            lastPriceUpdate: state.user?.id === user?.id && state.user?.isAdmin === user?.isAdmin
                ? state.lastPriceUpdate : null,
        }));
    },

    updateLastPriceUpdate: (metadata) => {
        set(state => ({
            lastPriceUpdate: mergeLastPriceUpdate(state.lastPriceUpdate, metadata, state.user?.isAdmin),
        }));
    },

    clearUser: () => {
        set({
            user: null,
            mode: "user",
            lastPriceUpdate: null,
        });
    },


    fetchSnapshotV2: async () => {
        return fetchSnapshotV2({
            get,
            set,
            user: get().user,
        });
    },   

    postStepPrice: async ({ nodeId, data }) => {
        const prev = clone(get().quantitiesById);

        try {
            const response = await PostGoldStepPrice(data);
            const updated = response.data;

            // set(state => ({
            //     quantitiesById: {
            //         ...state.quantitiesById,
            //         [nodeId]: {
            //             ...state.quantitiesById[nodeId],
            //             steps:{
            //                 ...state.quantitiesById[nodeId].steps,
            //             ['buy']: {
            //                 ...state.quantitiesById[nodeId].steps['buy'],
            //                 value: updated.stepValue,
            //             },
            //             ['sell']: {
            //                 ...state.quantitiesById[nodeId].steps['sell'],
            //                 value: updated.stepValue,
            //             },
            //             }
            //         }
            //     }
            // }));

            // close UI toggle after success
            // get().toggleStepButtonType(nodeId);

            return updated;

        } catch (error) {
            // console.error("postStepPrice failed:", error);

            // optional rollback (if needed)
            // set({ quantitiesById: prev });

            throw error;
        }
    },

    postGoldPrice: async ({ quantityDefinition, price, priceType }) => {
        try {
            const payload = {
                price: price,
                priceType: priceType,
                goldType: quantityDefinition.goldType,
            };
            if(quantityDefinition.type === 'direct'){
                payload['quantityDefinition'] = quantityDefinition.id
            }else{
                payload['parentQuantityDefinition'] = quantityDefinition.id
            }

            const res = await PostGoldPrice(payload);

            const results = res.data;

            // 🔥 UPDATE STORE HERE
            const key =
                priceType === 1 ? "buy" : "sell";

        // set(state => {
        //     const updated = { ...state.quantitiesById };
        //     for (const item of results['child']) {
        //         const qdId = item.quantityDefinition;
        //         const pqId = item.parentQuantityDefinition
        //         const priceType = item.priceType;
        //         const nodeId = `q${item.quantityDefinition}`;

        //         const qd = updated[nodeId];
        //         if (!qd) continue;

        //         const isBuy = priceType === state.buyType?.value;
        //         const key = isBuy ? "buy" : "sell";

        //         updated[nodeId] = {
        //             ...qd,
        //             prices: {
        //                 ...qd.prices,
        //                 [key]: {"price": item.price,
        //                         "basePrice": item.base_price
        //                 },
        //             },
        //         };
        //     }
        //     for (const item of results['parent']) {
        //         const qdId = item.quantityDefinition;
        //         const pqId = item.parentQuantityDefinition
        //         const priceType = item.priceType;
        //         const nodeId = `p${item.parentQuantityDefinition}`;

        //         const qd = updated[nodeId];
        //         if (!qd) continue;

        //         const isBuy = priceType === state.buyType?.value;
        //         const key = isBuy ? "buy" : "sell";

        //         updated[nodeId] = {
        //             ...qd,
        //             prices: {
        //                 ...qd.prices,
        //                 [key]: {"price": item.price,
        //                     "basePrice": item.base_price
        //                 },
        //             },
        //         };
        //     }
        //     return { quantitiesById: updated };
        // });

            return results;

        } catch (err) {
            console.error("postGoldPrice failed:", err);
        }
    },

    postGoldPriceV2: async ({ quantityDefinition, price, priceType }) => {
        try {
            const payload = {
                price: price,
                priceType: priceType,
                goldType: quantityDefinition.goldType,
            };
            if(quantityDefinition.type !== 'parent'){
                payload['quantityDefinition'] = quantityDefinition.id
                const res = await PostLastGoldPrice(payload);
                const results = res.data;
                return results;
            } else {
                payload['parentQuantityDefinition'] = quantityDefinition.id
                const res = await PostParentGoldPrice(payload);
                const results = res.data;
                return results;
            }

        } catch (err) {
            console.error("postGoldPrice failed:", err);
        }
    },

    // -------------------------
    // ACTIVATE / DEACTIVATE QD
    // -------------------------
    toggleQuantityActive: async (q, qdId, data) => {

        const prev = clone(get().quantitiesById);
        try {
            const response = await QuantityDefinitionPatch(qdId, data);

            // const updated = normalizeQuantity(response.data);

            // set((state) => ({
            //     quantitiesById: {
            //         ...state.quantitiesById,

            //         [updated.nodeId]: {
            //             ...state.quantitiesById[updated.nodeId],

            //             isActive: updated.isActive,

            //             active: {
            //                 ...state.quantitiesById[updated.nodeId].active,

            //                 buy: {
            //                     id: updated.active.buy.id,
            //                     isActive: updated.active.buy.isActive,
            //                 },

            //                 sell: {
            //                     id: updated.active.sell.id,
            //                     isActive: updated.active.sell.isActive,
            //                 },
            //             },
            //         },
            //     },
            // }));

            // return updated;

        } catch (e) {
            console.error('error is: ', e);

            // set({
            //     quantitiesById: prev,
            // });
        }
    },
    // -------------------------
    // GOLD PRICE ACTIVE TOGGLE
    // -------------------------
   toggleGoldPriceActive: async (q, priceType) => {
        const key = get().getToggleKey(q, priceType);
        const prev = clone(get().quantitiesById);
        const side = priceType === 1 ? 'buy' : 'sell';
        
        // Set loading state
        set(state => ({
            loadingStates: { ...state.loadingStates, [key]: true },
            errorStates: { ...state.errorStates, [key]: null }
        }));
        
        try {
            const data = {
                isActive: !q.active[side].isActive
            };
            
            const res = await PatchGoldPriceIsActive(data, q, priceType);
            
            // Update store on success and clear loading
            set(state => ({
                // quantitiesById: {
                //     ...state.quantitiesById,
                //     [q.nodeId]: {
                //         ...state.quantitiesById[q.nodeId],
                //         active: {
                //             ...state.quantitiesById[q.nodeId].active,
                //             [side]: {
                //                 ...state.quantitiesById[q.nodeId].active[side],
                //                 isActive: res.data['is_active']
                //             }
                //         }
                //     }
                // },
                loadingStates: { ...state.loadingStates, [key]: false },
                errorStates: { ...state.errorStates, [key]: null }
            }));
            
            return res;
            
        } catch (e) {
            console.error('Toggle failed:', e);
            
            // Rollback and clear loading
            set(state => ({
                quantitiesById: prev,
                loadingStates: { ...state.loadingStates, [key]: false },
                errorStates: { ...state.errorStates, [key]: e.message || 'Operation failed' }
            }));
            
            throw e;
        }
    },

    togglePricesActivation: async (priceType) => {
    const state = get();

    const isBuy = priceType?.value === state.buyType?.value;

    const current = isBuy
        ? state.buyPricesIsActive
        : state.sellPricesIsActive;

    const nextValue = !current;

    try {
        const data = new FormData();
        data.append("isActive", nextValue);

        const res = await GeneralPatchGoldPriceIsActive(
            data,
            0,
            priceType?.value
        );

        const updated = res.data.goldPricesIsActive;

        set({
            ...(isBuy
                ? { buyPricesIsActive: updated }
                : { sellPricesIsActive: updated }),
        });

    } catch (e) {
        console.error(e);
    }
    },

    fetchPricesActivation: async () => {
        const { buyType, sellType } = get();

        try {
            const [buyRes, sellRes] = await Promise.all([
                GeneralGetGoldPriceIsActive(0, buyType?.value),
                GeneralGetGoldPriceIsActive(0, sellType?.value),
            ]);

            set({
                buyPricesIsActive: buyRes.data.goldPricesIsActive,
                sellPricesIsActive: sellRes.data.goldPricesIsActive,
            });

        } catch (e) {
            console.error(e);
        }
    },

    // -------------------------
    // WEBSOCKET PATCH
    // -------------------------
    applyPatch: (patch) => {
        return applyPatchCore({
            set,
            get,
            patch,
        });
    },


    updateQuantityDefinition: async (quantityDefinition, data) => {
        const prev = clone(get().quantityDefinitionsById);

        try {
            const response = await QuantityDefinitionPatch(quantityDefinition.id, data);

            const updated = response.data;
            set(state => ({
                quantitiesById: {
                    ...state.quantitiesById[quantityDefinition.nodeId],
                    isActive: updated.isActive,
                    active: {
                        ...state.quantitiesById[quantityDefinition.nodeId].active,
                        buy: updated.buy_price_is_active,
                        sell: updated.sell_price_is_active
                    }
                },
            }));

            return updated;

        } catch (e) {
            console.error(e);
            set({ quantitiesById: prev });
        }
    },


    toggleGoldPriceActivation: async (qdId, nodeId, priceType) => {
        const state = get();

        const field =
            priceType === state.buyType?.value
                ? "buy_price_is_active"
                : "sell_price_is_active";

        const current = state.quantitiesById[nodeId]?.[field];

        if (!current) return;

        try {
            const data = { isActive: !current.is_active };

            const res = await GeneralPatchGoldPriceIsActive(
                data,
                qdId,
                priceType
            );

            const updated = res?.data?.goldPricesIsActive;

            if (!updated) return;

            // ONLY update on success
            set(state => ({
                quantitiesById: {
                    ...state.quantitiesById,
                    [nodeId]: {
                        ...state.quantitiesById[nodeId],
                        [field]: updated,
                    }
                }
            }));

        } catch (e) {
            console.error("toggleGoldPriceActivation failed:", e);
        }
    },

    applyQuantityDefinitionRealtime: (message) => {
        const parsed = message?.message || message;

        const state = get();

        if (!parsed?.id) return;

        const existing = state.quantitiesById[parsed.id];
        if (!existing) return;

        set(state => ({
            quantitiesById: {
                ...state.quantitiesById,
                [parsed.id]: {
                    ...state.quantitiesById[parsed.id],
                    ...parsed,
                },
            },
        }));
    },

    bulkPostGoldPricesV2: async (requests, scope) => {
        return bulkPostGoldPricesV2({
            requests,
            scope,
            set,
        });
    },

}));